import TranslationWrapper from "~/components/translations/TranslationWrapper";
import React from "react";
import { Translation } from "~/shared/types/translation";
import { useSearchTranslations } from "./translations";
import { ImSpinner7 } from "react-icons/im";
import SpinnerTw from "~/components/SpinnerTw";
import { Skeleton } from "@mui/material";

export type TranslateToElement = ReturnType<typeof useTranslationsByLabel>['t']
export type TranslateToString = ReturnType<typeof useTranslationsByLabel>['ts']

export const useTranslationsByLabel = ({
  site_id,
  locale,
  labels,
}: {
  site_id: number,
  locale: string,
  // this must be passed "as const" when calling this hook or type checking 
  // will fail on t['my_label']
  labels: string[],
}) => {
  const response = useSearchTranslations({
    site_id,
    locales_filter: locale,
    labels_filter: labels.join(' '),
  })

  const {
    data: translations,
    isSuccess: isTranslationsSuccess,
    isLoading: isTranslationsLoading,
  } = response;

  type TranslationByLabel = {
    [label: string]: Translation
  };

  let translationsByLabel = {} as TranslationByLabel;

  if (isTranslationsSuccess) {
    translations?.forEach(translation => {
      translationsByLabel[translation.label] = translation;
    });
  }

  const getTranslationByLabel = (label: string) => {
    const missingTranslation = {
      label,
      locale,
      text: isTranslationsLoading ? <Skeleton variant="text" /> : `${locale}.${label}`,
      site_id,
      type: 'Translation'
    }
    return translationsByLabel[label] || missingTranslation
  }


  // translate with TranslationWrapper
  const t = (
    label: string,
    opts: {
      html?: boolean,
      wrap?: boolean,
      replacements?: Parameters<typeof TranslationWrapper>[0]['replacements']
    } = {}) => {
    return (
      <TranslationWrapper
        translation={getTranslationByLabel(label)}
        html={opts.html}
        wrap={opts.wrap}
        replacements={opts.replacements}
      />
    )
  }

  // string-only, no wrapper
  const ts = (label: string) => {
    const text = getTranslationByLabel(label).text

    // sometimes the text attr is a react node (when loading)
    // so we can't show that in the title
    // we could in theory show the label here, but unlear SEO remifications
    return (
      typeof text === 'object' ? '' : text
    )
  }

  return {
    t,
    ts,
    translations,
    isTranslationsSuccess,
    isTranslationsLoading,
    response
  }
};

